import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Clock, CheckCircle, Circle, FileText, ListChecks, ChatCircle, House, Check, CalendarBlank, Warning, Phone, EnvelopeSimple, MapTrifold } from "@phosphor-icons/react";
import { usePresenceWebSocket } from "@/hooks/usePresenceWebSocket";
import { PresenceBar } from "@/components/app/PresenceBar";
import { getCaseProgress } from "@/lib/caseProgress";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageIcons = {
  completed: <CheckCircle weight="fill" className="w-7 h-7 text-emerald-600" />,
  current: <Circle weight="bold" className="w-7 h-7 text-blue-600 animate-pulse-ring" />,
  pending: <Circle weight="regular" className="w-7 h-7 text-slate-300" />,
};

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPortal = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? "This link is invalid or has expired." : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);

  // Live updates: refetch on any case-level event
  const handleWsMessage = useCallback((msg) => {
    if (["stage_updated", "stages_updated", "document_uploaded", "documents_bulk_uploaded", "agent_notes_updated"].includes(msg.type)) {
      fetchPortal();
    }
  }, [fetchPortal]);
  const { connected, peers, typingActors } = usePresenceWebSocket(token ? `/api/ws/portal/${token}` : null, handleWsMessage);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="portal-error">
        <div style={{ background: '#100f5d' }}>
          <div className="h-[3px]" style={{ background: '#c87d3a' }} />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2.5">
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-9 w-auto" />
            <span className="text-xs text-slate-300 ml-1">Client Portal</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-slate-50">
          <div className="text-center max-w-sm px-6">
            <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#100f5d' }}>Link Not Found</h2>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const { case: caseData, tasks, documents, notes_count, solicitor, agent_notes } = data;
  const agentNotes = agent_notes || [];
  const getProgress = () => getCaseProgress(caseData.stages || []);
  const getCurrentStage = () => caseData.stages?.find(s => s.status === "current")?.label || "Complete";
  const pendingTasks = tasks.filter(t => t.status !== "completed");
  const completedTasks = tasks.filter(t => t.status === "completed");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      {/* Header */}
      <header className="sticky top-0 z-50 shadow-sm" style={{ background: '#100f5d' }}>
        <div className="h-[3px]" style={{ background: '#c87d3a' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-9 w-auto" />
            <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#e89e63' }}>Client Portal</span>
          </div>
          <a href="tel:01603339174" className="hidden sm:flex items-center text-xs text-white hover:underline" style={{ fontFamily: 'Manrope, sans-serif' }}>
            01603 339174
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="relative bg-white border border-slate-200 rounded-xl overflow-hidden mb-8 shadow-sm" data-testid="portal-hero">
          <div className="relative h-36 sm:h-44 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1710883734889-5a0b8ab6bfcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBVSyUyMGhvdXNlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc2MTM3NDg0fDA&ixlib=rb-4.1.0&q=85"
              alt="Property" className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full mb-2 ${
                    caseData.case_type === "buying" ? "bg-blue-500/90 text-white" : "bg-amber-500/90 text-white"
                  }`}>
                    <House weight="fill" className="w-3 h-3 mr-1" />
                    {caseData.case_type === "buying" ? "Buying" : "Selling"}
                  </span>
                  <h1 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{caseData.title}</h1>
                  <p className="text-sm text-slate-300 flex items-center gap-1 mt-0.5"><MapPin weight="fill" className="w-3.5 h-3.5" />{caseData.property_address}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{getProgress()}%</span>
                  <p className="text-xs text-slate-300">complete</p>
                </div>
              </div>
            </div>
          </div>
          <div className="px-5 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
            {caseData.case_ref && (
              <span data-testid="portal-case-ref">Your case ref: <strong className="text-slate-700 font-mono">{caseData.case_ref}</strong></span>
            )}
            <span>Solicitor: <strong className="text-slate-700">{caseData.solicitor_name}</strong></span>
            <span>Current Stage: <strong className="text-slate-700">{getCurrentStage()}</strong></span>
            <span>Started: <strong className="text-slate-700">{new Date(caseData.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</strong></span>
            <PresenceBar peers={peers} typingActors={typingActors} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Timeline */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="portal-timeline">
              <h2 className="text-base font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Conveyancing Progress</h2>
              <div className="relative">
                {caseData.stages?.map((stage, idx) => {
                  const isLast = idx === caseData.stages.length - 1;
                  return (
                    <div key={stage.key} className={`relative flex gap-4 animate-fade-in-up stagger-${Math.min(idx + 1, 6)}`} data-testid={`portal-stage-${stage.key}`}>
                      <div className="flex flex-col items-center">
                        <div className="flex-shrink-0 z-10 bg-white p-0.5">{stageIcons[stage.status] || stageIcons.pending}</div>
                        {!isLast && <div className={`w-0.5 flex-1 min-h-[36px] ${stage.status === "completed" ? "bg-emerald-200" : "bg-slate-200"}`} />}
                      </div>
                      <div className={`flex-1 ${isLast ? "pb-0" : "pb-7"}`}>
                        <h4 className={`text-sm font-semibold ${stage.status === "completed" ? "text-slate-900" : stage.status === "current" ? "text-blue-700" : "text-slate-400"}`}
                          style={{ fontFamily: 'Outfit, sans-serif' }}>{stage.label}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            stage.status === "completed" ? "bg-emerald-50 text-emerald-700"
                              : stage.status === "current" ? "bg-blue-50 text-blue-700"
                              : "bg-slate-100 text-slate-400"}`}>
                            {stage.status === "current" && <Clock weight="bold" className="w-3 h-3" />}
                            {stage.status === "completed" ? "Completed" : stage.status === "current" ? "In Progress" : "Pending"}
                          </span>
                          {stage.completed_at && <span className="text-xs text-slate-400">{new Date(stage.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
                          {stage.custom && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">Custom</span>}
                          {stage.subcategories?.length > 0 && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                              stage.subcategories.filter(s => s.completed).length === stage.subcategories.length
                                ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                            }`}>
                              {stage.subcategories.filter(s => s.completed).length}/{stage.subcategories.length}
                            </span>
                          )}
                        </div>
                        {stage.notes && <p className="text-xs text-slate-500 mt-1.5">{stage.notes}</p>}
                        {stage.subcategories?.length > 0 && (
                          <div className="mt-2 space-y-1 pl-0.5">
                            {stage.subcategories.map((sub) => (
                              <div key={sub.id} className="flex items-center gap-2" data-testid={`portal-sub-${stage.key}-${sub.id}`}>
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                                  sub.completed ? "bg-emerald-600 border-emerald-600" : "border-slate-300"
                                }`}>
                                  {sub.completed && <Check weight="bold" className="w-2 h-2 text-white" />}
                                </div>
                                <span className={`text-xs ${sub.completed ? "text-slate-400 line-through" : "text-slate-600"}`}>
                                  {sub.label}
                                </span>
                                {sub.completed_at && (
                                  <span className="text-[10px] text-slate-400">
                                    {new Date(sub.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            {/* Tasks */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="portal-tasks">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ListChecks weight="bold" className="w-4 h-4 text-slate-500" /> Tasks ({tasks.length})
              </h3>
              {tasks.length === 0 ? (
                <p className="text-xs text-slate-400 py-3">No tasks yet</p>
              ) : (
                <div className="space-y-2">
                  {pendingTasks.map((t, i) => {
                    const isOverdue = t.due_date && new Date(t.due_date) < new Date(new Date().toISOString().split("T")[0]);
                    return (
                      <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${isOverdue ? "border-red-200 bg-red-50/30" : "border-slate-100"}`}>
                        <div className="w-4 h-4 mt-0.5 rounded border-2 border-slate-300 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-900">{t.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {t.due_date && (
                              <span className={`text-[10px] flex items-center gap-0.5 ${isOverdue ? "text-red-600 font-medium" : "text-slate-400"}`}>
                                <CalendarBlank weight="bold" className="w-2.5 h-2.5" />
                                {new Date(t.due_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                              </span>
                            )}
                            {isOverdue && <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-red-100 text-red-600 flex items-center gap-0.5"><Warning weight="fill" className="w-2 h-2" />Overdue</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {completedTasks.map((t, i) => (
                    <div key={`done-${i}`} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-slate-100 opacity-60">
                      <div className="w-4 h-4 mt-0.5 rounded bg-emerald-600 border-2 border-emerald-600 flex items-center justify-center shrink-0">
                        <Check weight="bold" className="w-2.5 h-2.5 text-white" />
                      </div>
                      <p className="text-xs text-slate-400 line-through">{t.title}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Case Updates from your solicitor — auto-populated when a
                milestone/stage is ticked off, and augmented with ad-hoc
                messages the solicitor posts through the case file. */}
            {agentNotes.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="portal-agent-notes">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <ChatCircle weight="bold" className="w-4 h-4 text-slate-500" /> Case Updates
                  <span className="ml-1 text-[10px] font-medium text-slate-400 uppercase tracking-wider">({agentNotes.length})</span>
                </h3>
                <ol className="space-y-3">
                  {agentNotes.map((n) => (
                    <li key={n.id} className="border-l-2 border-blue-500 pl-3 py-1" data-testid={`portal-agent-note-${n.id}`}>
                      <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap">{n.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {n.created_by_name || "Solicitor"} · {new Date(n.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {n.edited_at && <span className="italic"> · edited</span>}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Documents */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="portal-documents">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileText weight="bold" className="w-4 h-4 text-slate-500" /> Documents ({documents.length})
              </h3>
              {documents.length === 0 ? (
                <p className="text-xs text-slate-400 py-3">No documents yet</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                        <FileText weight="fill" className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-900 truncate">{d.original_filename}</p>
                        <p className="text-[10px] text-slate-400">{d.uploaded_by_name} · {new Date(d.created_at).toLocaleDateString("en-GB")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes count */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ChatCircle weight="bold" className="w-4 h-4 text-slate-500" /> {notes_count} message{notes_count !== 1 ? "s" : ""} exchanged
              </h3>
              <p className="text-xs text-slate-400 mt-1">Sign in to view and send messages.</p>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5" data-testid="portal-reply-disclaimer">
                  <Clock weight="bold" className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" />
                  <span>Whilst this is an instant message, replies follow standard response times. We aim to review messages within <span className="font-medium text-slate-600">48 hours</span> — at busy times this may be longer.</span>
                </p>
              </div>
            </div>

            {/* Solicitor Contact */}
            {solicitor && solicitor.name && (
              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="portal-solicitor-contact">
                <h3 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Solicitor</h3>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center shrink-0">
                    <span className="text-white text-xs font-bold">{solicitor.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-900">{solicitor.name}</p>
                </div>
                <div className="space-y-2 text-xs">
                  {solicitor.email && (
                    <a href={`mailto:${solicitor.email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
                      <EnvelopeSimple weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{solicitor.email}</span>
                    </a>
                  )}
                  {solicitor.phone && (
                    <a href={`tel:${solicitor.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
                      <Phone weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{solicitor.phone}</span>
                    </a>
                  )}
                  {solicitor.office_address && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapTrifold weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{solicitor.office_address}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center border-t border-slate-200 pt-6 pb-4">
          <p className="text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Powered by <strong className="text-slate-600">UKC Legal Solicitors</strong> · This is a read-only view of your conveyancing progress.
          </p>
        </div>
      </main>
    </div>
  );
}
